/* eslint-disable no-console */
/* eslint-disable no-param-reassign */
import { useKeycloak } from '@react-keycloak/web';
import React, { useReducer, useEffect, useState } from 'react';

import { set, get } from 'idb-keyval';
import PropTypes from 'prop-types';

import { Mixpanel, trackingMessages } from 'Services';
import { defaultCompany } from 'Services/API/company.service';

import { GET_TOKEN, GET_USER, SET_COMPANY } from './actionTypes';
import SDPContext from './Context';
import { initialState, combinedReducers } from './Reducer';

export default function SDPProvider({ children }) {
  const { keycloak, initialized } = useKeycloak();
  const [state, dispatch] = useReducer(combinedReducers, initialState);
  const [userPerms, setUserPerms] = useState([]);
  const [identified, setIdentified] = useState(false);

  function setGroupAndIdentifyOnMixpanel(company, user) {
    try {
      Mixpanel.group.create(company.id);
      Mixpanel.group.set(company.id, {
        $name: company.name,
        id: company.id,
        is_trial: company.is_trial
      });

      if (!identified) {
        Mixpanel.identify(user.sub);
        Mixpanel.people.set({
          $first_name: user.given_name,
          $last_name: user.family_name,
          $email: user.email
        });
        Mixpanel.track(trackingMessages.log_in);
        setIdentified(true);
      }
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(async () => {
    if (state.user.data.sub && state.company.data.current.id) {
      setGroupAndIdentifyOnMixpanel(
        state.company.data.current,
        state.user.data
      );
    }
  }, [state.company.data.current]);

  function registerTokenInApp() {
    dispatch({ type: GET_TOKEN, payload: keycloak.token });
  }

  function registerCompanyInApp() {
    get('currentCompany')
      .then((currCompany) => {
        defaultCompany()
          .then(({ data: { entries } }) => {
            if (currCompany?.id) {
              entries = entries
                ? [
                    currCompany,
                    ...entries.filter((el) => el.id !== currCompany.id)
                  ]
                : [currCompany];
            }
            dispatch({ type: SET_COMPANY, payload: entries });
          })
          .catch(console.log);
      })
      .catch(console.log);
  }

  function registerUserPermissionsInApp() {
    const rawPerms =
      keycloak?.idTokenParsed?.permissions ||
      keycloak?.idTokenParsed?.realm_access?.roles;

    let perms = rawPerms
      ?.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
      ?.map((el) => el.split(':'))
      ?.reduce((permRes, permEl) => {
        const hasEl = permRes
          ?.filter((el) => el.account_id === permEl[0])
          ?.shift();

        return !hasEl
          ? [...permRes, { account_id: permEl[0], permissions: [permEl[1]] }]
          : permRes.map((el) =>
              el.account_id === hasEl.account_id
                ? { ...el, permissions: [...el.permissions, permEl[1]] }
                : el
            );
      }, []);

    // TODO: remove async storage
    const permsRes = JSON.parse(localStorage.getItem('perms'));

    if (!permsRes?.length) {
      localStorage.setItem('perms', JSON.stringify(perms));
    }
    set('perms', perms);
    setUserPerms(perms);
  }

  // faz o refesh do token a cada 5 segundos
  useEffect(() => {
    const refreshToken = setInterval(() => {
      if (initialized) {
        if (keycloak.authenticated) {
          keycloak
            .updateToken(30)
            .then((refreshed) => {
              if (refreshed) {
                registerTokenInApp();
                registerUserPermissionsInApp();
              }
            })
            .catch((err) => console.log(err));
        }
      }
    }, 10000);

    return () => clearInterval(refreshToken);
  }, [initialized]);

  // inicia os dados do keycloak e persiste no estado do SDP Context
  useEffect(() => {
    const clearToken = () => {
      dispatch({ type: GET_TOKEN, payload: '' });
    };
    if (initialized) {
      if (keycloak.authenticated) {
        registerTokenInApp();
        registerUserPermissionsInApp();
        registerCompanyInApp();
        keycloak
          .loadUserInfo()
          .then((user) => {
            dispatch({
              type: GET_USER,
              payload: { ...user, permissions: userPerms }
            });
          })
          .catch((err) => console.log(err));
      } else {
        clearToken();
      }
    }

    return clearToken;
  }, [initialized]);

  return (
    <SDPContext.Provider value={{ state, dispatch }}>
      {children}
    </SDPContext.Provider>
  );
}

SDPProvider.propTypes = {
  children: PropTypes.node.isRequired
};
